import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { TMachineMaintenanceState } from '../reducers';
import {
  TFilterSelected,
  TREQUEST_BULK_UPDATE_STATUS,
  TREQUEST_BULK_ASSIGN_LOCATIONS,
  TREQUEST_MACHINE_TRANSFER,
  TRESPONSE_MACHINE_LIST,
  TListingFetchReason,
  TREQUEST_BULK_UPDATE_PRINTED_STATUS,
  TREQUEST_TRANSFER_MACHINE_TO_SIBLING_FACTORY,
  TREQUEST_BULK_UPDATE_PREVENTIVE_SCHEDULE,
  TREQUEST_UPDATE_MACHINE,
  TREQUEST_TRANSFER_BETWEEN_FLOATING_AREAS,
  TMachineListingTab,
  TREQUEST_RETURN_MACHINES_TO_VENDOR,
  TREQUEST_REACTIVATE_RETURNED_MACHINES,
  TM8MachineId,
  TMachineDetails,
} from '@monorepo/types';
import * as MachineListActions from './machine-list.actions';
import * as MachineListSelector from './machine-list.selectors';
import { TUserPaginationOption } from '@monorepo/ui';
import { LIST_ITEMS_PER_PAGE_LIST } from '../../constants/constants';
import { LOCAL_STORAGE_UTILS } from '@monorepo/utils';
import { AppNotificationUtilClass, MachineMaintenanceDataService } from '@monorepo/services';
import { catchError, finalize, lastValueFrom, map, of, take } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class MachineListFacade {
  constructor(
    private store: Store<TMachineMaintenanceState>,
    private APIHelper: AppNotificationUtilClass,
    private mmDataService: MachineMaintenanceDataService,
  ) {
    this.initializePagination();
  }

  initializePagination() {
    const storedPageSize = LOCAL_STORAGE_UTILS.retrieveData('machineListPageSize');
    if (storedPageSize && LIST_ITEMS_PER_PAGE_LIST.includes(Number(storedPageSize))) {
      this.setPagination({
        pageNo: 1,
        pageSize: parseInt(storedPageSize),
      });
    } else {
      LOCAL_STORAGE_UTILS.storeData('machineListPageSize', LIST_ITEMS_PER_PAGE_LIST[0].toString());
      this.setPagination({
        pageNo: 1,
        pageSize: LIST_ITEMS_PER_PAGE_LIST[0],
      });
    }
  }

  setPagination(pagination: TUserPaginationOption) {
    LOCAL_STORAGE_UTILS.storeData('machineListPageSize', pagination.pageSize.toString());
    this.store.dispatch(new MachineListActions.SetMachineListPagination(pagination));
  }

  resetMachineListInfo(fetchReason: 'ACTION') {
    const pageSize = Number(LOCAL_STORAGE_UTILS.retrieveData('machineListPageSize') || LIST_ITEMS_PER_PAGE_LIST[0]);
    this.setPagination({ pageNo: 1, pageSize });
    this.store.pipe(select(MachineListSelector.getUserSelectedFilters), take(1)).subscribe((res) => {
      this.fetchMachineListInfo(fetchReason, res);
    });
  }

  fetchMachineListInfo(fetchReason: TListingFetchReason, selectedFilters: TFilterSelected[], searchStrOpt?: string) {
    this.store.dispatch(new MachineListActions.GetMachineListInfo(fetchReason, selectedFilters, searchStrOpt));
  }

  deleteMachineListRow(machineIds: TM8MachineId[]) {
    this.store.dispatch(new MachineListActions.DeleteMachines(machineIds));
  }

  async updateMachineListRow(machineId: TM8MachineId, updatedMachine: TREQUEST_UPDATE_MACHINE['details']) {
    const loaderEl = await this.APIHelper.presentLoading();
    const dismiss = () => this.APIHelper.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.mmDataService.updateMachine({ machineId, details: updatedMachine }).pipe(
        map(() => true),
        catchError(() => of(false)),
        finalize(dismiss),
      ),
    );
  }

  bulkUpdateMachineStatus(payload: TREQUEST_BULK_UPDATE_STATUS) {
    return this.store.dispatch(new MachineListActions.BulkUpdateStatus(payload));
  }

  bulkUpdateMachinePrintedStatus(payload: TREQUEST_BULK_UPDATE_PRINTED_STATUS) {
    return this.store.dispatch(new MachineListActions.BulkUpdatePrintedStatus(payload));
  }

  fetchMachineListDropdown() {
    this.store.dispatch(new MachineListActions.GetMachineListFilterDropDownInfo());
  }

  fetchBulkAssignLocationDropdown() {
    this.store.dispatch(new MachineListActions.GetBulkAssignLocationDropdown());
  }

  assignBulkLocation(payload: TREQUEST_BULK_ASSIGN_LOCATIONS) {
    this.store.dispatch(new MachineListActions.BulkAssignLocation(payload));
  }

  async transferBetweenFloatingAreas(payload: TREQUEST_TRANSFER_BETWEEN_FLOATING_AREAS) {
    const loaderEl = await this.APIHelper.presentLoading();
    const dismiss = () => this.APIHelper.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.mmDataService.transferBetweenFloatingAreas(payload).pipe(
        map(() => true),
        catchError(() => of(false)),
        finalize(dismiss),
      ),
    );
  }

  fetchSiblingFactoriesDropdown() {
    this.store.dispatch(new MachineListActions.GetSiblingFactoriesDropdown());
  }

  async transferToSiblingFactory(payload: TREQUEST_TRANSFER_MACHINE_TO_SIBLING_FACTORY) {
    const loaderEl = await this.APIHelper.presentLoading();
    const dismiss = () => this.APIHelper.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.mmDataService.transferToSiblingFactory(payload).pipe(
        map(() => true),
        catchError(() => of(false)),
        finalize(dismiss),
      ),
    );
  }

  bulkUpdatePreventiveSchedule(payload: TREQUEST_BULK_UPDATE_PREVENTIVE_SCHEDULE) {
    this.store.dispatch(new MachineListActions.BulkUpdatePreventiveSchedule(payload));
  }

  setListingTab(tab: TMachineListingTab) {
    this.store.dispatch(new MachineListActions.SetMachineListingTab(tab));
  }

  async returnMachinesToVendor(machineIds: TREQUEST_RETURN_MACHINES_TO_VENDOR['machineIds']) {
    const loaderEl = await this.APIHelper.presentLoading();
    const dismiss = () => this.APIHelper.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.mmDataService.returnMachinesToVendor({ machineIds }).pipe(
        map(() => true),
        catchError(() => of(false)),
        finalize(dismiss),
      ),
    );
  }

  async reactivateReturnedMachines(machineIds: TREQUEST_REACTIVATE_RETURNED_MACHINES['machineIds']) {
    const loaderEl = await this.APIHelper.presentLoading();
    const dismiss = () => this.APIHelper.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.mmDataService.reactivateReturnedMachines({ machineIds }).pipe(
        map(() => true),
        catchError(() => of(false)),
        finalize(dismiss),
      ),
    );
  }

  fetchMachineTransferDropdown(machineId: TM8MachineId) {
    this.store.dispatch(new MachineListActions.MachineTransferDropdown(machineId));
  }

  fetchMachineAndLineDetails(
    factorySerialNumber: TRESPONSE_MACHINE_LIST['machines'][number]['machine']['factorySerialNumber'],
    machineId: TM8MachineId,
  ) {
    this.store.dispatch(new MachineListActions.GetMachineAndLineDetails(factorySerialNumber, machineId));
  }

  resetMachineAndLineDetails() {
    this.store.dispatch(new MachineListActions.ResetMachineAndLineDetails());
  }

  machineTransfer(payload: TREQUEST_MACHINE_TRANSFER) {
    this.store.dispatch(new MachineListActions.MachineTransfer(payload));
  }

  getMachineListDropdown() {
    return this.store.pipe(select(MachineListSelector.getMachineListDropdown));
  }

  getSiblingFactoriesDropdown() {
    return this.store.pipe(select(MachineListSelector.getSiblingFactoriesDropdown));
  }

  getMachineListInfo() {
    return this.store.pipe(select(MachineListSelector.getMachineListInfo));
  }

  getBulkAssignLocationDropdown() {
    return this.store.pipe(select(MachineListSelector.getBulkAssignLocationDropdown));
  }

  getMachineTransferDropdown() {
    return this.store.pipe(select(MachineListSelector.getMachineTransferDropdown));
  }

  getMachineAndLineDetails() {
    return this.store.pipe(select(MachineListSelector.getMachineAndLineDetails));
  }

  getPagination() {
    return this.store.pipe(select(MachineListSelector.getPagination));
  }

  getListingTab() {
    return this.store.pipe(select(MachineListSelector.getListingTab));
  }

  async getMachinesBySerialNumber(factorySerialNumber: TMachineDetails['factorySerialNumber']) {
    const loaderEl = await this.APIHelper.presentLoading();
    const dismiss = () => this.APIHelper.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.mmDataService.getMachinesBySerialNumber(factorySerialNumber).pipe(
        catchError(() => of(null)),
        finalize(dismiss),
      ),
    );
  }
}

export type TGetMachineListDropdown = ReturnType<MachineListFacade['getMachineListDropdown']>;
export type TGetMachineListInfo = ReturnType<MachineListFacade['getMachineListInfo']>;
export type TGetBulkAssignLocationDropdown = ReturnType<MachineListFacade['getBulkAssignLocationDropdown']>;
export type TGetSiblingFactoriesDropdown = ReturnType<MachineListFacade['getSiblingFactoriesDropdown']>;
export type TGetMachineTransferDropdown = ReturnType<MachineListFacade['getMachineTransferDropdown']>;
export type TGetMachineAndLineDetails = ReturnType<MachineListFacade['getMachineAndLineDetails']>;
export type TGetMachineListPagination = ReturnType<MachineListFacade['getPagination']>;
export type TGetMachineListingTab = ReturnType<MachineListFacade['getListingTab']>;
